import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useAuth } from "../../hooks/useAuth";
import {
  add_support_tickets_api,
  get_ticket_detail_api,
  update_support_tickets_api,
} from "../../DAL/SupportTickets";
import toast from "react-hot-toast";
import { useContent } from "../../hooks/useContent";
import { useNavigate, useParams } from "react-router-dom";
import FormButtons from "../../Components/GeneralComponents/FormButtons";
import PageLoader from "../../Components/GeneralComponents/PageLoader";

const CreateOrUpdateTicket = () => {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const { ticket_id } = useParams();
  const { setPageHeading } = useContent();
  const [inputs, setInputs] = useState({
    case_title: "",
    case_description: "",
    Category: "",
    status: "ACTIVE",
    priority: "MEDIUM",
    assignee: "CF ADMIN",
    customername: userInfo.partner_username || "",
    company_contact_first_name: userInfo.first_name,
    company_contact_mid_name: "",
    company_contact_last_name: userInfo.last_name,
    company_contact_email: userInfo.email,
    company_contact_phone: userInfo.phone_mobile,
    newupdate: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate] = useState(
    new Date().toISOString().slice(0, 19).replace("T", " ")
  );
  const [isLoadingPage, setIsLoadingPage] = useState(true);

  const getTicketDetail = async () => {
    const result = await get_ticket_detail_api(ticket_id);
    if (result.code === 200) {
      setInputs((old) => ({ ...old, ...result.ticket }));
    } else {
      toast.error(`${result.message}`);
    }
    setIsLoadingPage(false);
  };

  useEffect(() => {
    setPageHeading(`${ticket_id ? "Update" : "Create"}  Support Ticket`);
    if (ticket_id) {
      getTicketDetail();
    } else {
      setIsLoadingPage(false);
    }
  }, [ticket_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs({ ...inputs, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
  };

  const validate = () => {
    const newErrors = {};
    if (!inputs.case_title.trim())
      newErrors.case_title = "Case Title is required.";
    if (!inputs.case_description.trim())
      newErrors.case_description = "Description is required.";
    if (!inputs.company_contact_email.trim())
      newErrors.company_contact_email = "Email is required.";
    if (!inputs.company_contact_phone.trim())
      newErrors.company_contact_phone = "Phone is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    const result = ticket_id
      ? await update_support_tickets_api(inputs, ticket_id)
      : await add_support_tickets_api(inputs);
    if (result.code === 200) {
      toast.success(
        `Ticket ${ticket_id ? "updated" : "created"} successfully.`
      );
      handleCancel();
    } else {
      toast.error(`${result.message}`);
    }
    setIsLoading(false);
  };

  const handleCancel = () => navigate("/support-tickets");

  if (isLoadingPage) return <PageLoader />;

  return (
    <div className="login-page">
      <div className="d-flex justify-content-center">
        <div className="container mx-auto">
          <div className="form-wrapper">
            <form onSubmit={handleSubmit}>
              <div className="d-flex justify-content-end align-items-center mb-4">
                <FormButtons {...{ isLoading, handleCancel }} />
              </div>
              {ticket_id && (
                <div className="row mb-3">
                  <div className="col-md-4 form-group">
                    <label>Case Number</label>
                    <div className="input-wrapper">
                      <span className="input-icon">#</span>
                      <input
                        type="text"
                        value={ticket_id}
                        readOnly
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-md-4 form-group">
                    <label>Status</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:progress-clock" className="input-icon" />
                      <select
                        name="status"
                        value={inputs.status}
                        className="form-control"
                        disabled
                      >
                        {[
                          "ACTIVE",
                          "CLOSED",
                          "IN-PROGRESS",
                          "WAITING FOR DETAIL",
                          "COMPLETED",
                        ].map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4 form-group">
                    <label>Last Update</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:calendar-clock" className="input-icon" />
                      <input
                        type="text"
                        value={lastUpdate}
                        readOnly
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-8">
                  <div className="form-group mb-3">
                    <label>Case Title *</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:format-title" className="input-icon" />
                      <input
                        type="text"
                        name="case_title"
                        value={inputs.case_title}
                        onChange={handleChange}
                        className={`form-control ${
                          errors.case_title ? "is-invalid" : ""
                        }`}
                        placeholder=""
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-4 form-group">
                  <label>Priority</label>
                  <div className="input-wrapper">
                    <Icon icon="mdi:alert-circle" className="input-icon" />
                    <select
                      name="priority"
                      value={inputs.priority}
                      onChange={handleChange}
                      className="form-control"
                    >
                      {["LOW", "MEDIUM", "HIGH"].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="form-group mb-3">
                <label>Description *</label>
                <div className="input-wrapper">
                  <textarea
                    name="case_description"
                    rows="4"
                    value={inputs.case_description}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.case_description ? "is-invalid" : ""
                    }`}
                  ></textarea>
                </div>
              </div>
              {/* <div className="row mb-3">
                                <div className="col-md-6 form-group">
                                    <label>Category</label>
                                    <div className="input-wrapper">
                                        <Icon icon="mdi:folder-outline" className="input-icon" />
                                        <select
                                            name="Category"
                                            value={inputs.Category}
                                            onChange={handleChange}
                                            className="form-control"
                                        >
                                            <option value="">Select Category</option>
                                            {["WEB", "iOS APP", "ANDROID APP", "ADMIN PANEL", "OTHER"].map((cat) => (
                                                <option key={cat} value={cat}>
                                                    {cat}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-6 form-group">
                                    <label>Assignee</label>
                                    <div className="input-wrapper">
                                        <Icon icon="mdi:account-tie" className="input-icon" />
                                        <input type="text" name="assignee" value={inputs.assignee} readOnly className="form-control" />
                                    </div>
                                </div>
                            </div> */}
              <h4 className="mt-4 mb-2 middle-heading">Customer Info</h4>
              <div className="row mb-3">
                <div className="col-md-6 form-group">
                  <label>First Name</label>
                  <div className="input-wrapper">
                    <Icon icon="mdi:account-edit" className="input-icon" />
                    <input
                      type="text"
                      name="company_contact_first_name"
                      value={inputs.company_contact_first_name}
                      onChange={handleChange}
                      className="form-control"
                      disabled
                    />
                  </div>
                </div>

                <div className="col-md-6 form-group">
                  <label>Last Name</label>
                  <div className="input-wrapper">
                    <Icon icon="mdi:account-outline" className="input-icon" />
                    <input
                      type="text"
                      name="company_contact_last_name"
                      value={inputs.company_contact_last_name}
                      onChange={handleChange}
                      className="form-control"
                      disabled
                    />
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6 form-group">
                  <label>Email *</label>
                  <div className="input-wrapper">
                    <Icon icon="mdi:email-outline" className="input-icon" />
                    <input
                      type="email"
                      name="company_contact_email"
                      value={inputs.company_contact_email}
                      onChange={handleChange}
                      className={`form-control ${
                        errors.company_contact_email ? "is-invalid" : ""
                      }`}
                      disabled
                    />
                  </div>
                </div>
                <div className="col-md-6 form-group">
                  <label>Phone *</label>
                  <div className="input-wrapper">
                    <Icon icon="mdi:phone-outline" className="input-icon" />
                    <input
                      type="text"
                      name="company_contact_phone"
                      value={inputs.company_contact_phone}
                      onChange={handleChange}
                      className={`form-control ${
                        errors.company_contact_phone ? "is-invalid" : ""
                      }`}
                    />
                  </div>
                </div>
              </div>
              {ticket_id && (
                <div className="form-group mb-3">
                  <label>Provide New Update</label>
                  <div className="input-wrapper">
                    <textarea
                      name="newupdate"
                      rows="3"
                      value={inputs.newupdate}
                      onChange={handleChange}
                      className="form-control"
                    ></textarea>
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-end align-items-center mb-4">
                <FormButtons {...{ isLoading, handleCancel }} />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrUpdateTicket;
